// frontend/src/services/notificationApi.ts
// Frontend API service for notification management

import api from './api';

// Types for notification data
export interface Notification {
  id: number;
  notification_type: string;
  title: string;
  message: string;
  action_url?: string;
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  created_at: string;
  is_read: boolean;
}

// Hierarchical notification types
export interface NotificationGroup {
  group_name: string;
  display_name: string;
  description: string;
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  types: NotificationType[];
}

export interface NotificationType {
  type_name: string;
  group_name: string;
  display_name: string;
  description: string;
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  default_enabled: boolean;
}

export interface HierarchicalPreference {
  user_id: number;
  preference_type: 'GROUP' | 'TYPE';
  preference_name: string;
  enabled: boolean;
}

// Legacy preferences interface (for backward compatibility)
export interface NotificationPreferences {
  user_id: string;
  email_enabled: boolean;
  in_app_enabled: boolean;
  infra_monitoring: boolean;
  services_monitoring: boolean;
  infra_user_alerts: boolean;
  infra_resource_events: boolean;
  license_events: boolean;
  quiet_hours_start?: string;
  quiet_hours_end?: string;
  digest_frequency: 'immediate' | 'daily' | 'weekly';
}

// New hierarchical preferences
export interface NotificationDeliverySettings {
  user_id: number;
  email_enabled: boolean;
  in_app_enabled: boolean;
  quiet_hours_start?: string;
  quiet_hours_end?: string;
  digest_frequency: 'immediate' | 'daily' | 'weekly';
  timezone: string;
}

export interface NotificationResponse {
  success: boolean;
  data: Notification[];
  unreadCount: number;
}

// Get user's notifications
export const getUserNotifications = async (markAsRead = false): Promise<NotificationResponse> => {
  try {
    const response = await api.get('/notifications', {
      params: markAsRead ? { markAsRead: 'true' } : {}
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching notifications:', error);
    throw error;
  }
};

// Mark notification as read
export const markNotificationAsRead = async (notificationId: number): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.put(`/notifications/${notificationId}/read`);
    return response.data;
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
};

// Delete notification
export const deleteNotification = async (notificationId: number): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.delete(`/notifications/${notificationId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting notification:', error);
    throw error;
  }
};

// Get user notification preferences
export const getUserPreferences = async (): Promise<{ success: boolean; data: NotificationPreferences }> => {
  try {
    const response = await api.get('/notifications/preferences');
    return response.data;
  } catch (error) {
    console.error('Error fetching user preferences:', error);
    throw error;
  }
};

// Update user notification preferences
export const updateUserPreferences = async (preferences: Partial<NotificationPreferences>): Promise<{ success: boolean; message: string; data: any }> => {
  try {
    const response = await api.put('/notifications/preferences', preferences);
    return response.data;
  } catch (error) {
    console.error('Error updating user preferences:', error);
    throw error;
  }
};

// Subscribe to notification group
export const subscribeToGroup = async (groupName: string): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.post(`/notifications/groups/${groupName}/subscribe`);
    return response.data;
  } catch (error) {
    console.error('Error subscribing to group:', error);
    throw error;
  }
};

// Unsubscribe from notification group
export const unsubscribeFromGroup = async (groupName: string): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.delete(`/notifications/groups/${groupName}/subscribe`);
    return response.data;
  } catch (error) {
    console.error('Error unsubscribing from group:', error);
    throw error;
  }
};

// Get notification statistics (admin only)
export const getNotificationStats = async (): Promise<{ success: boolean; data: any }> => {
  try {
    const response = await api.get('/admin/notifications/stats');
    return response.data;
  } catch (error) {
    console.error('Error fetching notification stats:', error);
    throw error;
  }
};

// Send test notification (admin only)
export const sendTestNotification = async (data: {
  userId: string;
  notification_type: string;
  title: string;
  message: string;
}): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.post('/admin/notifications/test', data);
    return response.data;
  } catch (error) {
    console.error('Error sending test notification:', error);
    throw error;
  }
};

// ===== HIERARCHICAL NOTIFICATION API METHODS =====

// Get notification hierarchy (groups with their types)
export const getNotificationHierarchy = async (): Promise<{ success: boolean; data: NotificationGroup[] }> => {
  try {
    const response = await api.get('/notifications/hierarchy');
    return response.data;
  } catch (error) {
    console.error('Error fetching notification hierarchy:', error);
    throw error;
  }
};

// Get user's hierarchical preferences
export const getHierarchicalPreferences = async (): Promise<{ success: boolean; data: HierarchicalPreference[] }> => {
  try {
    const response = await api.get('/notifications/preferences/hierarchical');
    return response.data;
  } catch (error) {
    console.error('Error fetching hierarchical preferences:', error);
    throw error;
  }
};

// Update hierarchical preferences
export const updateHierarchicalPreferences = async (preferences: HierarchicalPreference[]): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.put('/notifications/preferences/hierarchical', { preferences });
    return response.data;
  } catch (error) {
    console.error('Error updating hierarchical preferences:', error);
    throw error;
  }
};

// Get delivery settings
export const getDeliverySettings = async (): Promise<{ success: boolean; data: NotificationDeliverySettings }> => {
  try {
    const response = await api.get('/notifications/delivery-settings');
    return response.data;
  } catch (error) {
    console.error('Error fetching delivery settings:', error);
    throw error;
  }
};

// Update delivery settings
export const updateDeliverySettings = async (settings: Partial<NotificationDeliverySettings>): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.put('/notifications/delivery-settings', settings);
    return response.data;
  } catch (error) {
    console.error('Error updating delivery settings:', error);
    throw error;
  }
};

// Format time ago for display
export const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;

  return date.toLocaleDateString();
};

// Get notification icon based on type
export const getNotificationIcon = (type: string): string => {
  const iconMap: { [key: string]: string } = {
    // Storage & Resources
    'STORAGE_THRESHOLD_80': 'pi pi-server',
    'STORAGE_THRESHOLD_90': 'pi pi-exclamation-triangle',
    'STORAGE_THRESHOLD_95': 'pi pi-exclamation-triangle',
    'HIGH_CPU_USAGE': 'pi pi-microchip',
    'HIGH_MEMORY_USAGE': 'pi pi-memory',

    // License Management
    'LICENSE_EXPIRY_30_DAYS': 'pi pi-calendar',
    'LICENSE_EXPIRY_15_DAYS': 'pi pi-calendar',
    'LICENSE_EXPIRY_7_DAYS': 'pi pi-calendar',
    'LICENSE_EXPIRY_3_DAYS': 'pi pi-calendar',
    'LICENSE_EXPIRY_1_DAY': 'pi pi-calendar',
    'LICENSE_EXTENSION_REQUESTED': 'pi pi-send',
    'LICENSE_EXTENSION_APPROVED': 'pi pi-check',
    'LICENSE_EXTENSION_REJECTED': 'pi pi-times',
    'LICENSE_EXTENSION_COMPLETED': 'pi pi-check-circle',

    // System Health
    'SERVER_HIGH_CPU': 'pi pi-microchip',
    'SERVER_HIGH_MEMORY': 'pi pi-memory',
    'SERVER_LOW_DISK': 'pi pi-hdd',
    'SERVICE_DOWN': 'pi pi-times-circle',
    'SERVICE_RESTARTED': 'pi pi-refresh',

    // User Account
    'ACCOUNT_EXPIRY_7_DAYS': 'pi pi-user',
    'ACCOUNT_EXPIRY_3_DAYS': 'pi pi-user',
    'ACCOUNT_EXPIRY_1_DAY': 'pi pi-user',
    'PASSWORD_EXPIRY_WARNING': 'pi pi-lock',

    // Assignments
    'ASSIGNMENT_DEADLINE_7_DAYS': 'pi pi-file',
    'ASSIGNMENT_DEADLINE_3_DAYS': 'pi pi-file',
    'ASSIGNMENT_DEADLINE_1_DAY': 'pi pi-file'
  };

  return iconMap[type] || 'pi pi-bell';
};

// Get notification severity color
export const getNotificationSeverity = (priority: string): 'success' | 'info' | 'warn' | 'error' => {
  switch (priority) {
    case 'CRITICAL': return 'error';
    case 'HIGH': return 'error';
    case 'MEDIUM': return 'warn';
    case 'LOW': return 'info';
    default: return 'info';
  }
};

// Group names for display
export const GROUP_DISPLAY_NAMES: { [key: string]: string } = {
  'INFRA_MONITORING': 'Infrastructure Monitoring',
  'SERVICES_MONITORING': 'Services Monitoring',
  'INFRA_USER_ALERTS': 'User Alerts',
  'INFRA_RESOURCE_EVENTS': 'Resource Events',
  'LICENSE_EVENTS': 'License Events'
};

// Group descriptions
export const GROUP_DESCRIPTIONS: { [key: string]: string } = {
  'INFRA_MONITORING': 'Server health, maintenance notifications, and infrastructure status changes',
  'SERVICES_MONITORING': 'Site reachability, service status, and container health monitoring',
  'INFRA_USER_ALERTS': 'Storage quota alerts, assignment deadlines, and user-specific notifications',
  'INFRA_RESOURCE_EVENTS': 'High CPU/memory usage alerts and resource utilization warnings',
  'LICENSE_EVENTS': 'License expiry warnings, utilization alerts, and server status notifications'
};

const notificationApi = {
  getUserNotifications,
  markNotificationAsRead,
  deleteNotification,
  getUserPreferences,
  updateUserPreferences,
  subscribeToGroup,
  unsubscribeFromGroup,
  getNotificationStats,
  sendTestNotification,
  formatTimeAgo,
  getNotificationIcon,
  getNotificationSeverity,
  GROUP_DISPLAY_NAMES,
  GROUP_DESCRIPTIONS,
  // Hierarchical API methods
  getNotificationHierarchy,
  getHierarchicalPreferences,
  updateHierarchicalPreferences,
  getDeliverySettings,
  updateDeliverySettings
};
export default notificationApi;
